/**
 * Переиспользуемый компонент пагинации
 * Дата: 10.02.2026
 * 
 * Используется в:
 * - Appointments.tsx — пагинация записей
 * - Services.tsx — пагинация услуг
 * - Patients.tsx — пагинация пациентов (TODO-052)
 * - PhoneBinding.tsx — пагинация результатов поиска
 * - Templates.tsx — пагинация шаблонов
 * - Messages.tsx — пагинация бесед
 * 
 * Связан с: components/ui/index.ts (экспорт), components/ui/Button.tsx
 * 
 * КАК РАБОТАЕТ:
 * - Принимает offset, limit, total
 * - Показывает "Показано X—Y из Z"
 * - Кнопки Назад / Вперёд
 * - Опционально: номера страниц
 * - Вызывает onOffsetChange при смене страницы
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/utils/cn';

export interface PaginationProps {
  /** Текущий offset (0-based) */
  offset: number;
  /** Количество элементов на странице */
  limit: number;
  /** Общее количество элементов */
  total: number;
  /** Callback при смене offset */
  onOffsetChange: (offset: number) => void;
  /** Показывать текст "Показано X—Y из Z" */
  showInfo?: boolean;
  /** Показывать номера страниц (если false — только Назад/Вперёд) */
  showPageNumbers?: boolean;
  /** Максимальное количество номеров страниц */
  maxPageNumbers?: number;
  /** Размер кнопок */
  size?: 'sm' | 'md';
  /** Дополнительные CSS классы */
  className?: string;
  /** Disabled состояние */
  disabled?: boolean;
  /** data-testid для тестов */
  testId?: string;
}

/**
 * Pagination — унифицированный компонент пагинации
 * 
 * @example
 * // Базовое использование
 * <Pagination 
 *   offset={offset} 
 *   limit={20} 
 *   total={total} 
 *   onOffsetChange={setOffset} 
 * />
 * 
 * @example
 * // С номерами страниц
 * <Pagination 
 *   offset={offset} 
 *   limit={20} 
 *   total={150} 
 *   onOffsetChange={setOffset}
 *   showPageNumbers
 *   maxPageNumbers={5}
 * />
 */
export function Pagination({
  offset,
  limit,
  total,
  onOffsetChange,
  showInfo = true,
  showPageNumbers = false,
  maxPageNumbers = 5,
  size = 'sm',
  className,
  disabled = false,
  testId,
}: PaginationProps) {
  // Не показывать пагинацию если всё помещается на одну страницу
  if (total <= limit) return null;

  const currentPage = Math.floor(offset / limit);
  const totalPages = Math.ceil(total / limit);
  const isFirstPage = offset === 0;
  const isLastPage = offset + limit >= total;
  const showFrom = offset + 1;
  const showTo = Math.min(offset + limit, total);

  const handlePrev = () => {
    if (!isFirstPage) {
      onOffsetChange(Math.max(0, offset - limit));
    }
  };

  const handleNext = () => {
    if (!isLastPage) {
      onOffsetChange(offset + limit);
    }
  };

  const handleGoToPage = (page: number) => {
    onOffsetChange(page * limit);
  };

  // Вычислить видимые номера страниц
  const getVisiblePages = (): number[] => {
    if (totalPages <= maxPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const half = Math.floor(maxPageNumbers / 2);
    let start = Math.max(0, currentPage - half);
    let end = start + maxPageNumbers;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(0, end - maxPageNumbers);
    }

    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  return (
    <div 
      className={cn('flex items-center justify-between', className)}
      data-testid={testId}
    >
      {/* Информация */}
      {showInfo && (
        <div className="text-sm text-shell-muted">
          Показано {showFrom}—{showTo} из {total}
        </div>
      )}

      {/* Навигация */}
      <div className="flex items-center gap-1">
        {/* Кнопка Назад */}
        <Button
          variant="ghost"
          size={size}
          onClick={handlePrev}
          disabled={isFirstPage || disabled}
          testId={testId ? `${testId}-prev` : undefined}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Назад
        </Button>

        {/* Номера страниц */}
        {showPageNumbers && (
          <div className="flex items-center gap-1 mx-2">
            {getVisiblePages()[0] > 0 && (
              <>
                <PageButton
                  page={0}
                  currentPage={currentPage}
                  size={size}
                  disabled={disabled}
                  onClick={handleGoToPage}
                />
                {getVisiblePages()[0] > 1 && (
                  <span className="px-1 text-shell-muted text-sm">…</span>
                )}
              </>
            )}

            {getVisiblePages().map((page) => (
              <PageButton
                key={page}
                page={page}
                currentPage={currentPage}
                size={size}
                disabled={disabled}
                onClick={handleGoToPage}
              />
            ))}

            {getVisiblePages()[getVisiblePages().length - 1] < totalPages - 1 && (
              <>
                {getVisiblePages()[getVisiblePages().length - 1] < totalPages - 2 && (
                  <span className="px-1 text-shell-muted text-sm">…</span>
                )}
                <PageButton
                  page={totalPages - 1}
                  currentPage={currentPage}
                  size={size}
                  disabled={disabled}
                  onClick={handleGoToPage}
                />
              </>
            )}
          </div>
        )}

        {/* Без номеров — просто текст текущей страницы */}
        {!showPageNumbers && (
          <span className="text-sm text-shell-muted mx-2">
            {currentPage + 1} / {totalPages}
          </span>
        )}

        {/* Кнопка Вперёд */}
        <Button
          variant="ghost"
          size={size}
          onClick={handleNext}
          disabled={isLastPage || disabled}
          testId={testId ? `${testId}-next` : undefined}
        >
          Вперёд
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/** Кнопка номера страницы */
function PageButton({ 
  page, 
  currentPage, 
  size, 
  disabled, 
  onClick 
}: { 
  page: number; 
  currentPage: number; 
  size: 'sm' | 'md'; 
  disabled: boolean; 
  onClick: (page: number) => void;
}) {
  const isActive = page === currentPage;
  return (
    <button
      type="button"
      onClick={() => onClick(page)}
      disabled={disabled || isActive}
      className={cn(
        'rounded-lg font-medium transition-colors duration-150',
        size === 'sm' ? 'px-2.5 py-1 text-sm min-w-[32px]' : 'px-3 py-1.5 text-base min-w-[36px]',
        isActive
          ? 'bg-shell-primary-soft text-shell-primary-strong cursor-default'
          : 'text-shell-muted hover:bg-shell-hover hover:text-shell-text',
        disabled && !isActive && 'opacity-50 cursor-not-allowed'
      )}
    >
      {page + 1}
    </button>
  );
}
